import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  CreateClientScheduledActionMutation,
  CreateClientScheduledActionMutationVariables, CreateGroupScheduledActionMutation,
  CreateGroupScheduledActionMutationVariables, CreateScheduledActionDto,
  DeleteScheduledActionMutation, DeleteScheduledActionMutationVariables,
  GetAllScheduledActionsQuery, GetAllScheduledActionsQueryVariables, GetScheduledActionQuery,
  GetScheduledActionQueryVariables, UpdateScheduledActionDto,
  UpdateScheduledActionMutation, UpdateScheduledActionMutationVariables,
} from '~/types/graphql';
import createClientScheduledAction  from '~/graphql/mutations/createClientScheduledAction.graphql';
import createGroupScheduledAction  from '~/graphql/mutations/createGroupScheduledAction.graphql';
import updateScheduledAction  from '~/graphql/mutations/updateScheduledAction.graphql';
import deleteScheduledAction  from '~/graphql/mutations/deleteScheduledAction.graphql';
import getAllScheduledActions  from '~/graphql/queries/getAllScheduledActions.graphql';
import getScheduledAction  from '~/graphql/queries/getScheduledAction.graphql';

export const useScheduledActionsStore = defineStore('scheduled-actions', () => {
  type ScheduledActionList = NonNullable<GetAllScheduledActionsQuery['getAllScheduledActions']>;
  type ScheduledActionListItem = ScheduledActionList[number];

  const loading = ref(false);
  const initialized = ref(false);
  const actions = ref<ScheduledActionList>([])

  function setAll(nextActions: ScheduledActionList) {
    actions.value = [...nextActions];
  }

  function upsertAction(nextAction: ScheduledActionListItem) {
    const index = actions.value.findIndex(action => action.id === nextAction.id);
    if (index === -1) {
      actions.value = [nextAction, ...actions.value];
      return;
    }

    const merged = { ...actions.value[index], ...nextAction } as ScheduledActionListItem;
    actions.value.splice(index, 1, merged);
  }

  function removeAction(id: number) {
    actions.value = actions.value.filter(action => action.id !== id);
  }

  async function hydrateSingleAction(id: number) {
    const single = await getAction(id);
    const action = single?.getScheduledAction;
    if (!action) return;
    upsertAction(action as unknown as ScheduledActionListItem);
  }

  async function createClientScheduledActions(input: CreateScheduledActionDto): Promise<CreateClientScheduledActionMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<CreateClientScheduledActionMutation, CreateClientScheduledActionMutationVariables>(
        {
          mutation: createClientScheduledAction,
          variables: {
            input
          },
        },
        'Error creating scheduled action'
      );

      const created = res?.data?.createClientScheduledAction;
      if (created) {
        upsertAction(created as unknown as ScheduledActionListItem);
        await hydrateSingleAction(created.id);
      }

      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function createGroupScheduledActions(input: CreateScheduledActionDto): Promise<CreateGroupScheduledActionMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<CreateGroupScheduledActionMutation, CreateGroupScheduledActionMutationVariables>(
        {
          mutation: createGroupScheduledAction,
          variables: {
            input: {
              ...input,
              extraData: typeof input.extraData === 'object' ? JSON.stringify(input.extraData) : input.extraData
            }
          }
        },
        'Error creating scheduled action'
      );

      const created = res?.data?.createGroupScheduledAction;
      if (created) {
        upsertAction(created as unknown as ScheduledActionListItem);
        await hydrateSingleAction(created.id);
      }

      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }


  async function updateAction(input: UpdateScheduledActionDto, actionId: number): Promise<UpdateScheduledActionMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<UpdateScheduledActionMutation, UpdateScheduledActionMutationVariables>({
        mutation: updateScheduledAction,
        variables: {
          actionId,
          input
        },
      },
        'Error updating scheduled action'
      );

      const updated = res?.data?.updateScheduledAction;
      if (updated) {
        upsertAction(updated as unknown as ScheduledActionListItem);
        await hydrateSingleAction(updated.id);
      }

      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function deleteAction(id: number): Promise<DeleteScheduledActionMutation | null> {
    loading.value = true;
    try {
     const res = await storeGraphqlMutation<DeleteScheduledActionMutation, DeleteScheduledActionMutationVariables>({
         mutation: deleteScheduledAction,
         variables: {
           id,
         },
       },
     );

      if (res?.data?.deleteScheduledAction) {
        removeAction(id);
      }

      return res?.data || null
    } finally {
      loading.value = false;
    }
  }

  async function getAllActions() {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetAllScheduledActionsQuery, GetAllScheduledActionsQueryVariables>({
        query: getAllScheduledActions,
        variables: {},
      },
        'Error fetching scheduled actions'
      );
      setAll(res?.data?.getAllScheduledActions ?? []);
      initialized.value = true;
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function getAction(id: number): Promise<GetScheduledActionQuery | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetScheduledActionQuery, GetScheduledActionQueryVariables>({
        query: getScheduledAction,
        variables: {
          id,
        },
      },
      'Error fetching scheduled action'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function ensureLoaded() {
    if (!initialized.value) {
      await getAllActions();
    }
  }

  return {
    loading,
    initialized,
    actions,
    ensureLoaded,
    createClientScheduledActions,
    createGroupScheduledActions,
    updateAction,
    deleteAction,
    getAllActions,
    getAction
  }
});